import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import type { DeletionBackupListItem } from '@his/shared';
import { get, post } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { DataTable, PageHeader } from '@/components/ui/layout';
import { Alert, Button } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';

/**
 * Escrow copies of purged hospitals (SRS 5.1).
 *
 * Every purge seals a copy of every row here first, and a purge with nowhere to
 * put one is refused. What you can do from this page is download the sealed
 * envelope; what you cannot do — from here or from any endpoint — is open it.
 * The private key lives on the owner's own machine, so a server that has been
 * taken over still cannot read a hospital it has already deleted.
 */
export function DeletionBackupsPage() {
  const backups = useQuery({
    queryKey: ['deletion-backups'],
    queryFn: () => get<DeletionBackupListItem[]>('/platform/deletion-backups'),
  });

  const [target, setTarget] = useState<DeletionBackupListItem | null>(null);

  return (
    <>
      <PageHeader
        title="Deletion backups"
        description="Encrypted copies taken automatically before each facility was purged"
      />

      <Alert className="mb-4">
        These are sealed with the platform&apos;s escrow public key and can only be opened with the
        private half, which never touches this server. Download one and run{' '}
        <code className="font-mono text-xs">
          node scripts/decrypt-tenant-backup.mjs &lt;file&gt; --key &lt;private.pem&gt;
        </code>{' '}
        on a machine that holds the key.
      </Alert>

      <DataTable
        rows={backups.data}
        loading={backups.isLoading} refreshing={backups.isFetching}
        error={backups.error}
        rowKey={(row) => row.id}
        empty="Nothing has been purged."
        columns={[
          {
            header: 'Facility',
            sort: (row) => row.tenantName,
            cell: (row) => (
              <div>
                <div className="font-medium">{row.tenantName}</div>
                <div className="text-xs text-muted-foreground">{row.tenantSlug}</div>
              </div>
            ),
          },
          { header: 'Reason', sort: (row) => row.reason, cell: (row) => <span className="text-muted-foreground">{row.reason}</span> },
          {
            header: 'Contents',
            align: 'right',
            sort: (row) => row.rowCount,
            cell: (row) => (
              <span className="tabular">
                {row.rowCount.toLocaleString()} rows · {row.tableCount} tables
              </span>
            ),
          },
          {
            header: 'Size',
            align: 'right',
            sort: (row) => row.sizeBytes,
            cell: (row) => <span className="tabular">{(row.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>,
          },
          { header: 'Taken', sort: (row) => row.createdAt, cell: (row) => dateTime(row.createdAt) },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button size="sm" variant="secondary" onClick={() => setTarget(row)}>
                <Download className="h-3.5 w-3.5" /> Download
              </Button>
            ),
          },
        ]}
      />

      {target && (
        <OwnerConfirmDialog<{ url: string; expiresAt: string }>
          open
          onClose={() => setTarget(null)}
          title="Download this escrow copy"
          description={`${target.tenantName} · the link works once, for 60 seconds`}
          confirmLabel="Download"
          action={(payload) => post<{ url: string; expiresAt: string }>(`/platform/deletion-backups/${target.id}/link`, payload)}
          onDone={(link) => {
            setTarget(null);
            window.location.assign(link.url);
          }}
        />
      )}
    </>
  );
}
